import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Backdrop, CircularProgress } from '@mui/material';
import Layout from '../../Components/Common/Layout/Layout';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import Loading from '../../Components/Loading/Loading';
import { errorMsg } from '../../constants/common.constant';
import useCompanyDirector from '../../hooks/data/useCompanyDirector';
import useContainerWidth from '../../hooks/useContainerWidth';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import CommonTooltip from '../../Components/Common/CommonTooltip/CommonTooltip';
import {
  BaseWidgetProps,
  CommonTooltipType,
  WidgetHeightMessage,
} from '../../types/common.type';
import {
  DirectorOrderBy,
  DirectorSortBy,
  WidgetNameSource,
} from '../../utils/type';
import './CompanyDirectorWidget.css';
import TableBasic from './components/TableBasic';
import TableExpanded from './components/TableExpanded';
import TableMobile from './components/TableMobile';
import { BREAKPOINTS } from '../../constants/breakpoints.constant';

export interface CompanyDirectorWidgetProps extends BaseWidgetProps {
  token?: string;
  symbol?: string;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

const CompanyDirectorWidget: React.FC<CompanyDirectorWidgetProps> = ({
  theme,
  token = '',
  symbol = '',
  onHeightChange,
  onError,
  onNoData,
  onLoadingChange,
}) => {
  const [sortBy, setSortBy] = useState<DirectorSortBy>(DirectorSortBy.Title);
  const [orderBy, setOrderBy] = useState<DirectorOrderBy>(DirectorOrderBy.Asc);

  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<CommonTooltipType>({
    content: '',
    show: false,
    x: 0,
    y: 0,
  });
  const { isSmallScreen, isLargeScreen, width } =
    useContainerWidth(containerRef);
  useResizeNotifier(
    containerRef,
    WidgetNameSource.CompanyDirector,
    onHeightChange,
  );

  const { data: directors, isLoading } = useCompanyDirector(
    symbol,
    token,
    sortBy,
    orderBy,
    onError,
    onNoData,
  );

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const currency =
    directors.find((d) => d.Latest_FYT_curr != null)?.Latest_FYT_curr ?? null;

  const handleSortChange = useCallback(
    (newSortBy: DirectorSortBy, newOrderBy: DirectorOrderBy) => {
      setSortBy(newSortBy);
      setOrderBy(newOrderBy);
    },
    [],
  );

  const renderTable = () => {
    if (isSmallScreen) {
      return (
        <TableMobile
          directors={directors}
          sortBy={sortBy}
          orderBy={orderBy}
          onSortChange={handleSortChange}
          setTooltip={setTooltip}
        />
      );
    }
    if (width >= BREAKPOINTS.SM && width < BREAKPOINTS.MD) {
      return (
        <TableBasic
          directors={directors}
          sortBy={sortBy}
          orderBy={orderBy}
          onSortChange={handleSortChange}
          currency={currency}
          setTooltip={setTooltip}
        />
      );
    }
    return (
      <TableExpanded
        directors={directors}
        sortBy={sortBy}
        orderBy={orderBy}
        onSortChange={handleSortChange}
        currency={currency}
        setTooltip={setTooltip}
      />
    );
  };

  return (
    <Layout theme={theme!}>
      <div className="company-director-container" ref={containerRef}>
        {isLoading && directors.length === 0 && !onLoadingChange ? (
          <div className="company-director-skeleton-loading-container">
            <Loading />
          </div>
        ) : !isLoading && directors.length === 0 ? (
          <ErrorMessage
            msg={errorMsg.find((m) => m.key === 'companyDirectors')}
          />
        ) : (
          <>
            {renderTable()}
            <Backdrop
              open={isLoading}
              sx={{
                position: 'absolute',
                zIndex: 5,
                backgroundColor: 'rgba(255, 255, 255, 0.4)',
              }}
            >
              <CircularProgress size={32} sx={{ color: 'var(--color-brand)' }} />
            </Backdrop>
            <CommonTooltip
              ref={tooltipRef}
              content={tooltip.content}
              show={tooltip.show}
              x={tooltip.x}
              y={tooltip.y}
            />
          </>
        )}
      </div>
    </Layout>
  );
};

export default CompanyDirectorWidget;
